// polyfills
import 'react-app-polyfill/ie11'
import 'core-js/stable'
import 'regenerator-runtime/runtime'
import 'raf/polyfill'

// on to the fun
import React from 'react'

/* Styles */
import '@foodsby/nutrient-v3/src/css/app.css'
import './styles/index.css'
import '@foodsby/nutrient/dist/nutrient.css'
import { themeConweb } from '@foodsby/nutrient-v3'
import * as Sentry from '@sentry/browser'
import ChangePassword from './components/changePassword/ChangePassword'
import CheckoutSidebar from './components/checkout/CheckoutSidebar'
import CompleteAccount from './components/complete-account/CompleteAccount'
import Confirmation from './components/confirmation/Confirmation'
import Contact from './components/contact-us/Contact'
import DefaultNav from './components/common/DefaultNav'
import ForgotPassword from './components/login/ForgotPassword'
import FriendRequest from './components/common/FriendRequest'
import HomeRedirect from './components/home/HomeRedirect'
import JoinPage from './components/join/JoinPage'
import LogRocket from 'logrocket'
import Logout from './components/login/Logout'
import Maintenance from './components/maintenance/Maintenance'
import NoLocationPage from './components/location-search/NoLocationPage'
import NotFound from './components/not-found/NotFound'
import OrderHistory from './components/history/OrderHistory'
import PageGiftRedemption from './pages/PageGiftRedemption'
import PersonalizeOrder from './components/personalize-order/PersonalizeOrder'
import PrivacyPolicyPage from './components/legal/PrivacyPolicyPage'
import ProfileSidebar from './components/profile/ProfileSidebar'
import ReactDOM from 'react-dom'
import SelectKioskPage from './components/location-search/SelectKioskPage'
import SelectLocationPage from './components/location-search/SelectLocationPage'
import SlackPage from './components/slack/SlackPage'
import Sticky from './components/common/Sticky'
import TermsOfUsePage from './components/legal/TermsOfUsePage'
import TermsOfUseRewardsPage from './components/legal/TermsOfUseRewardsPage'
import UnsubscribePage from './components/unsubscribe/UnsubscribePage'
import appboy from 'appboy-web-sdk' // Braze used to be called appboy
import pkg from '../package.json'
import setupLogRocketReact from 'logrocket-react'
import store, { history } from './redux/store'
import { ConnectedRouter } from 'connected-react-router'
import { Provider } from 'react-redux'
import { ProviderCheckout } from './providers/ProviderCheckout'
import { Route } from 'react-router'
import { Switch } from 'react-router-dom'
import { initTokens } from './util/webStorageUtils'
import { isWebpSupported } from '../src/util/fileUtils'
import { logException } from './util/errorUtils'
import { setWebpSupported } from '../src/redux/modules/browser'
import {
  changePasswordRoute,
  checkoutRoute,
  completeAccountRoute,
  confirmationRoute,
  contactRoute,
  creditsRoute,
  forgotPasswordRoute,
  friendsRoute,
  historyRoute,
  homeRoute,
  joinRoute,
  locationRoute,
  loginRoute,
  logoutRoute,
  maintenanceRoute,
  noLocationRoute,
  personalizeRoute,
  placeOrderRoute,
  privacyPolicyRoute,
  profileRoute,
  rootRoute,
  selectLocationRoute,
  slackRoute,
  subscriptionTermsAndConditionsRoute,
  termsAndConditionsRewardsRoute,
  termsAndConditionsRoute,
  unsubscribeRoute,
  referralRoute,
  referralTermsAndConditionsRoute,
  selectKioskRoute,
  redeemGiftRoute,
} from './routes/routes'
import {
  FooterLayoutRoute,
  PublicLayoutRoute,
  SidebarLayoutRoute,
  SimpleLayoutRoute,
} from './routes/layoutRoutes'
import { PlaceOrder, OrderNav, OrderSidebar } from './components/place-order'
import {
  PageCheckout,
  PageCredits,
  PageFriends,
  PageHome,
  PageLogin,
  PageProfile,
  PageReferral,
  PageReferralTermsAndConditions,
  PageSubscriptionTermsAndConditions,
} from './pages'
import { ThemeProvider } from 'styled-components'

// set up JWT
initTokens()
function inIframe() {
  try {
    return window.self !== window.top
  } catch (e) {
    return true
  }
}

// Update the old service worker if found
if ('serviceWorker' in navigator) {
  navigator.serviceWorker.getRegistrations().then(registrations => {
    // returns installed service workers
    if (registrations.length) {
      // eslint-disable-next-line
      for (let registration of registrations) {
        registration.update()
      }
    }
  })
}

// Setup sentry
Sentry.init({
  dsn: process.env.REACT_APP_SENTRY_DSN,
  release: `${pkg.name}@${pkg.version}`,
})

// Setup appboy
appboy.initialize(process.env.REACT_APP_BRAZE_API_KEY, {
  baseUrl: 'https://sdk.iad-03.braze.com/api/v3',
  enableLogging: process.env.NODE_ENV !== 'production',
  enableHtmlInAppMessages: true,
  minimumIntervalBetweenTriggerActionsInSeconds: 10,
  safariWebsitePushId: 'web.com.foodsby.order',
})
appboy.display.automaticallyShowNewInAppMessages()
appboy.openSession()
window.appboy = appboy
setInterval(() => appboy.requestContentCardsRefresh(), 15000)

async function renderApp() {
  try {
    // set up logrocket
    if (process.env.REACT_APP_LOGROCKET_ID) {
      LogRocket.init(process.env.REACT_APP_LOGROCKET_ID, {
        network: {
          requestSanitizer: function(req) {
            if (req.headers['Authorization']) {
              // remove token
              req.headers['Authorization'] = 'REDACTED'
            }
            if (req.url.indexOf('heartlandportico') > -1) {
              // remove heartland
              return null
            }
            if (
              req.url.indexOf('/oauth/token') > -1 ||
              req.url.indexOf('/uaa/passwords') > -1
            ) {
              // Remove tokens/passwords
              req.body = 'REDACTED'
            }

            return req
          },
          responseSanitizer: function(res) {
            if (
              res.url.indexOf('/oauth/token') > -1 ||
              res.url.indexOf('/uaa/passwords') > -1
            ) {
              // Remove tokens/passwords
              res.body = 'REDACTED'
            }
            return res
          },
        },
        console: {
          shouldAggregateConsoleErrors: true,
        },
        release: `${pkg.name}@${pkg.version}`,
      })
      setupLogRocketReact(LogRocket)
    }
    Sentry.configureScope(scope => {
      scope.addEventProcessor(async event => {
        event.extra.sessionURL = LogRocket.sessionURL
        return event
      })
    })

    // Setup webp support
    const hasWebpSupport = await isWebpSupported()
    store.dispatch(setWebpSupported(hasWebpSupport))

    // state
    const initialState = {
      subscription: { value: false },
    }

    const reducer = (state, action) => {
      switch (action.type) {
        case 'setSubscription':
          return {
            ...state,
            subscription: action.newSubscription,
          }

        default:
          return state
      }
    }

    // mount app
    const root = document.getElementById('root')
    if (root) {
      ReactDOM.render(
        <ThemeProvider theme={themeConweb}>
          <Provider store={store}>
            <ProviderCheckout initialState={initialState} reducer={reducer}>
              <ConnectedRouter history={history}>
                <Sticky>
                  <Switch>
                    <PublicLayoutRoute
                      component={PageLogin}
                      path={loginRoute.path}
                      title={loginRoute.title}
                    />
                    <PublicLayoutRoute
                      component={JoinPage}
                      path={joinRoute.path}
                      title={joinRoute.title}
                    />
                    <FooterLayoutRoute
                      component={PrivacyPolicyPage}
                      path={privacyPolicyRoute.path}
                      title={privacyPolicyRoute.title}
                    />
                    <FooterLayoutRoute
                      component={TermsOfUsePage}
                      path={termsAndConditionsRoute.path}
                      title={termsAndConditionsRoute.title}
                    />
                    <FooterLayoutRoute
                      component={TermsOfUseRewardsPage}
                      path={termsAndConditionsRewardsRoute.path}
                      title={termsAndConditionsRewardsRoute.title}
                    />
                    <PublicLayoutRoute
                      component={ChangePassword}
                      path={changePasswordRoute.path}
                      title={changePasswordRoute.title}
                    />
                    <PublicLayoutRoute
                      component={ForgotPassword}
                      path={forgotPasswordRoute.path}
                      title={forgotPasswordRoute.title}
                    />
                    <SimpleLayoutRoute
                      component={CompleteAccount}
                      path={completeAccountRoute.path}
                      title={completeAccountRoute.title}
                    />
                    <SidebarLayoutRoute
                      component={SelectKioskPage}
                      overrideAuthRedirect
                      path={selectKioskRoute.path}
                      sidebarRight={[]}
                      title={selectKioskRoute.title}
                    />
                    <SidebarLayoutRoute
                      component={SelectLocationPage}
                      overrideAuthRedirect
                      path={selectLocationRoute.path}
                      sidebarRight={[]}
                      title={selectLocationRoute.title}
                    />
                    <Route
                      component={HomeRedirect}
                      exact
                      path={homeRoute.path}
                      title={homeRoute.title}
                    />
                    <Route
                      component={HomeRedirect}
                      exact
                      path={rootRoute.path}
                      title={rootRoute.title}
                    />
                    <SidebarLayoutRoute
                      component={PageHome}
                      exact
                      overrideAuthRedirect
                      path={locationRoute.path}
                      showSubNav={true}
                      title={locationRoute.title}
                    />
                    <SidebarLayoutRoute
                      component={PlaceOrder}
                      exact
                      nav={OrderNav}
                      path={placeOrderRoute.path}
                      sidebarRight={[OrderSidebar]}
                      title={placeOrderRoute.title}
                    />
                    <SidebarLayoutRoute
                      component={PersonalizeOrder}
                      nav={OrderNav}
                      noScroll="true"
                      path={personalizeRoute.path}
                      showSubNav={true}
                      sidebarRight={OrderSidebar}
                      title={personalizeRoute.title}
                    />
                    <SidebarLayoutRoute
                      component={Confirmation}
                      nav={props => (
                        <DefaultNav pageName="Confirm Order" {...props} />
                      )}
                      path={confirmationRoute.path}
                      showSubNav={true}
                      title={confirmationRoute.title}
                    />
                    <SidebarLayoutRoute
                      component={OrderHistory}
                      nav={props => (
                        <DefaultNav pageName="Order History" {...props} />
                      )}
                      path={historyRoute.path}
                      title={historyRoute.title}
                    />
                    <SidebarLayoutRoute
                      component={PageCheckout}
                      nav={props => (
                        <DefaultNav pageName="Checkout" {...props} />
                      )}
                      path={checkoutRoute.path}
                      sidebarRight={[CheckoutSidebar]}
                      title={checkoutRoute.title}
                    />
                    <SidebarLayoutRoute
                      component={PageCredits}
                      nav={props => (
                        <DefaultNav pageName="Credits" {...props} />
                      )}
                      path={creditsRoute.path}
                      title={creditsRoute.title}
                    />
                    <SidebarLayoutRoute
                      component={PageProfile}
                      nav={props => (
                        <DefaultNav pageName="User Profile" {...props} />
                      )}
                      path={profileRoute.path}
                      sidebarRight={[ProfileSidebar]}
                      title={profileRoute.title}
                    />
                    <SidebarLayoutRoute
                      component={Contact}
                      nav={props => (
                        <DefaultNav pageName="Contact Us" {...props} />
                      )}
                      path={contactRoute.path}
                      title={contactRoute.title}
                    />
                    <SidebarLayoutRoute
                      component={PageFriends}
                      nav={props => (
                        <DefaultNav pageName="Friends" {...props} />
                      )}
                      path={friendsRoute.path}
                      sidebarRight={FriendRequest}
                      title={friendsRoute.title}
                    />
                    <PublicLayoutRoute
                      component={NoLocationPage}
                      path={noLocationRoute.path}
                      title={noLocationRoute.title}
                    />
                    <Logout path={logoutRoute.path} title={logoutRoute.title} />
                    <PublicLayoutRoute
                      component={Maintenance}
                      path={maintenanceRoute.path}
                      title={maintenanceRoute.title}
                    />
                    <PublicLayoutRoute
                      component={UnsubscribePage}
                      path={unsubscribeRoute.path}
                      title={unsubscribeRoute.title}
                    />
                    <SimpleLayoutRoute
                      component={SlackPage}
                      path={slackRoute.path}
                      title={slackRoute.title}
                    />
                    <FooterLayoutRoute
                      component={PageSubscriptionTermsAndConditions}
                      path={subscriptionTermsAndConditionsRoute.path}
                      title={subscriptionTermsAndConditionsRoute.title}
                    />
                    <SidebarLayoutRoute
                      component={PageGiftRedemption}
                      path={redeemGiftRoute.path}
                      title={redeemGiftRoute.title}
                    />
                    <FooterLayoutRoute
                      component={PageReferralTermsAndConditions}
                      path={referralTermsAndConditionsRoute.path}
                      title={referralTermsAndConditionsRoute.title}
                    />
                    <PublicLayoutRoute
                      component={PageReferral}
                      path={referralRoute.path}
                      title={referralRoute.title}
                    />
                    {/* This one goes last */}
                    <PublicLayoutRoute component={NotFound} title="Not found" />
                  </Switch>
                </Sticky>
              </ConnectedRouter>
            </ProviderCheckout>
          </Provider>
        </ThemeProvider>,
        root,
      )
    }
  } catch (ex) {
    logException(ex)
  }
}

// REDIRECT OLD URLS HERE
if (window.location.pathname.indexOf('/home/') > -1) {
  window.location = window.location.pathname.replace('/home', '')
} else if (!inIframe()) {
  renderApp()
}
